import { supabase } from '../lib/supabaseClient';
import { cachedFetch } from './cache';
import type { Branch, Warehouse } from '../types/domain';

/**
 * Reference-data reads (branches, warehouses). No permission gate of their
 * own in migration 0032 — visibility is tenant isolation only (RLS,
 * migration 0004), same as every prior FASE's reference tables. Used to let
 * a user acting under a tenant-wide role grant (branch_id = null in their
 * user_roles row) choose which branch/warehouse a given action applies to.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export async function listBranches(): Promise<Branch[]> {
  return cachedFetch('branches', async () => {
    const { data, error } = await supabase
      .from('branches')
      .select('branch_id, tenant_id, organization_id, branch_code, name, status')
      .eq('status', 'ACTIVE')
      .order('branch_code');
    if (error) throw new Error(`listBranches failed: ${error.message}`);
    return (data ?? []) as Branch[];
  });
}

/** Cached per branchId (or "all" when unscoped) — every operational page's
 * create-form warehouse picker calls this identically, so the cache key
 * MUST include branchId or switching branches would silently keep
 * serving the previous branch's warehouses. */
export async function listWarehouses(branchId?: string): Promise<Warehouse[]> {
  return cachedFetch(`warehouses:${branchId ?? 'all'}`, async () => {
    let query = supabase
      .from('warehouses')
      .select('warehouse_id, tenant_id, branch_id, warehouse_code, name, status')
      .eq('status', 'ACTIVE')
      .order('warehouse_code');
    if (branchId) {
      query = query.eq('branch_id', branchId);
    }
    const { data, error } = await query;
    if (error) throw new Error(`listWarehouses failed: ${error.message}`);
    return (data ?? []) as Warehouse[];
  });
}
